import { useContext, useEffect } from "react";

import { CmdKContext, CmdKState, SearchOption } from "./types";
import { useExperienceInfoOptions } from "./useExperienceInfoOptions";
import { useOrgListOptions } from "./useOrgListOptions";
import { useRouteOptions } from "./useRouteOptions";
import { useWorkflowCommandOptions } from "./useWorkflowCommandOptions";

export const useOptions = ({
  query,
  limit,
  state,
}: {
  query?: string;
  limit?: number;
  state: CmdKState;
}): SearchOption[] => {
  const defaultLimit = 5;
  const routeOptions = useRouteOptions(query, limit ?? defaultLimit);
  const experienceInfoOptions = useExperienceInfoOptions(
    state,
    query,
    limit ?? defaultLimit
  );
  const orgListOptions = useOrgListOptions(query, limit ?? defaultLimit);
  const workflowCommandOptions = useWorkflowCommandOptions(
    query,
    limit ?? defaultLimit
  );
  return [
    ...experienceInfoOptions,
    ...routeOptions,
    ...orgListOptions,
    ...workflowCommandOptions,
  ];
};

const wrapIndex = (index: number, arrayLength: number) => {
  if (arrayLength === 0) {
    return;
  }
  return ((index % arrayLength) + arrayLength) % arrayLength;
};

export const useKeyboardListener = () => {
  const { options, setState, select, close } = useContext(CmdKContext);

  useEffect(() => {
    const keyDown = (event: KeyboardEvent) => {
      const key = event.key;
      switch (key) {
        case "Escape":
          close(true);
          break;
        case "Enter":
          event.preventDefault();
          select();
          close();
          break;
        case "ArrowUp":
          event.preventDefault();
          setState((prev) => {
            const nxt = prev.highlighted ?? 0;
            prev.highlighted = wrapIndex(nxt - 1, options.length);
          });
          break;
        case "ArrowDown":
          event.preventDefault();
          setState((prev) => {
            const nxt = prev.highlighted ?? -1;
            prev.highlighted = wrapIndex(nxt + 1, options.length);
          });
          break;
        case "Shift":
          setState((prev) => {
            prev.secondaryAction = true;
          });
          break;
      }
    };

    const keyUp = (event: KeyboardEvent) => {
      const key = event.key;
      switch (key) {
        case "Shift":
          setState((prev) => {
            prev.secondaryAction = false;
          });
          break;
      }
    };

    window.addEventListener("keydown", keyDown);
    window.addEventListener("keyup", keyUp);
    return () => {
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
    };
  }, [options, select, close, setState]);
};
